import {
  Children,
  cloneElement,
  isValidElement,
  type ElementType,
  type ReactElement,
  type ReactNode,
} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { CaseRecord } from "../../types";
import type { CitationVerification } from "../../api";
import {
  buildCitationNumbering,
  indexRecords,
} from "../../lib/provenance";
import { renderFootnotes } from "../../lib/renderFootnotes";

// Recursively walk ReactMarkdown's children tree and render inline
// [rec:ID] citation tokens as subtle superscript footnotes on every
// plain-text leaf. React elements are cloned with their own children
// transformed, so tokens nested inside <strong>, <em>, <code>, table
// cells, headings etc. are covered too — otherwise they'd leak through as
// literal text.
function withCitations(
  children: ReactNode,
  numbering: Map<string, number>,
  tooltip: (id: string) => string | undefined,
  onNavigate: ((id: string) => void) | undefined,
  verification: ((id: string) => CitationVerification | undefined) | undefined,
  emphasizeFlagged: boolean,
): ReactNode {
  return Children.map(children, (child) => {
    if (typeof child === "string") {
      return renderFootnotes(child, {
        numbering,
        tooltip,
        onClick: onNavigate,
        verification,
        emphasizeFlagged,
      });
    }
    if (Array.isArray(child)) {
      return withCitations(child, numbering, tooltip, onNavigate, verification, emphasizeFlagged);
    }
    if (isValidElement(child)) {
      const props = (child as ReactElement<{ children?: ReactNode }>).props;
      if (props && props.children !== undefined) {
        return cloneElement(child as ReactElement<{ children?: ReactNode }>, {
          children: withCitations(
            props.children,
            numbering,
            tooltip,
            onNavigate,
            verification,
            emphasizeFlagged,
          ),
        });
      }
    }
    return child;
  });
}

// Shared renderer for an AI answer's markdown body with inline [rec:ID]
// source citations turned into clickable superscript footnotes. Used by
// both Interrogate's chat replies and Focused Answer so the citation
// experience is identical in every mode.
export default function AiAnswer({
  text,
  records,
  onNavigateToRecord,
  verification,
  emphasizeFlagged = false,
}: {
  text: string;
  records: CaseRecord[];
  onNavigateToRecord?: (recordId: string) => void;
  // Per-record gate results (spanning answer only): drives the per-citation
  // verification marks and their failure-naming tooltips.
  verification?: Record<string, CitationVerification>;
  emphasizeFlagged?: boolean;
}) {
  const numbering = buildCitationNumbering(text);
  const recordIndex = indexRecords(records);
  // Which gate(s) a cited record failed, in plain language — appended to the
  // record's date/subject tooltip so a flagged chip says exactly what to check.
  const failureReasons = (id: string): string[] => {
    const v = verification?.[id];
    if (!v) return [];
    const out: string[] = [];
    if (!v.citation_ok) out.push("citation could not be verified");
    if (!v.quote_ok) out.push("quote not verbatim in source");
    if (!v.date_ok) out.push("date not found in cited record");
    return out;
  };
  const tooltip = (id: string) => {
    const r = recordIndex.get(id);
    const desc = r ? r.label || r.subject : null;
    const base = r ? `${r.date}${desc ? ` — ${desc}` : ""}` : undefined;
    const reasons = failureReasons(id);
    if (reasons.length === 0) return base;
    const warn = `⚠ ${reasons.join("; ")}`;
    return base ? `${base} — ${warn}` : warn;
  };
  const verify = verification
    ? (id: string): CitationVerification | undefined => verification[id]
    : undefined;
  // Every markdown element that can hold plain text is wired through
  // withCitations so a stray [rec:...] token never leaks as raw text,
  // wherever the model put it (paragraph, bullet, heading, quote, table
  // cell, bold/em run).
  const wrap = (tag: ElementType) =>
    function WrappedElement({ children }: { children?: ReactNode }) {
      const T = tag;
      return (
        <T>
          {withCitations(
            children,
            numbering,
            tooltip,
            onNavigateToRecord,
            verify,
            emphasizeFlagged,
          )}
        </T>
      );
    };
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: wrap("p"),
        li: wrap("li"),
        h1: wrap("h1"),
        h2: wrap("h2"),
        h3: wrap("h3"),
        h4: wrap("h4"),
        h5: wrap("h5"),
        h6: wrap("h6"),
        blockquote: wrap("blockquote"),
        td: wrap("td"),
        th: wrap("th"),
        strong: wrap("strong"),
        em: wrap("em"),
      }}
    >
      {text}
    </ReactMarkdown>
  );
}
